// Static industry → sub-industry map. Verbatim port of the `subIndustryMap` JS object embedded in
// quralyst_research.html / strategic_research.html (no API — drives the dependent Sub-Industry Select).
// `industryList` is the exact 20-item ordered Industry list used by all three process pages.

export const industryList = [
  'Agriculture, Forestry & Fishing',
  'Mining & Metals',
  'Oil & Gas',
  'Utilities',
  'Construction & Engineering',
  'Manufacturing',
  'Transportation, Warehousing & Logistics',
  'Information Technology & Software Services',
  'Telecommunications',
  'Healthcare & Life Sciences',
  'Financial Services',
  'Insurance',
  'Real Estate, Property Development & Facilities Management',
  'Retail & E-commerce',
  'Consumer Packaged Goods',
  'Media, Entertainment & Publishing',
  'Hospitality, Travel & Tourism',
  'Aerospace & Defense',
  'Education & Training Services',
  'Professional & Business Services',
] as const;

export const subIndustryMap: Record<string, string[]> = {
  'Agriculture, Forestry & Fishing': [
    'Crop Production (grains, oilseeds, fruits, vegetables, nuts, greenhouse)',
    'Animal Production & Aquaculture (cattle, poultry, dairy, fish & seafood farms)',
    'Forestry & Logging (forest nurseries, logging)',
    'Fishing, Hunting & Trapping',
    'Agricultural Support Services',
  ],
  'Mining & Metals': [
    'Coal & Lignite Mining',
    'Metal Ore Mining (Ferrous, Non-ferrous)',
    'Non-metallic Mineral Mining & Quarrying (stone, sand & gravel, gypsum, potash, salt)',
    'Mining Support & Exploration Services (drilling, blasting, geological surveys)',
    'Metal Processing & Smelting (primary smelting, refining, metal rolling & drawing)',
  ],
  'Oil & Gas': [
    'Upstream (exploration, development)',
    'Oilfield Equipment & Services (drilling contractors, seismic, well services)',
    'Midstream (pipelines, gathering & processing, storage, LNG, transshipment)',
    'Downstream / Refining & Marketing (refineries, petrochemicals, fuel & lubricant marketing)',
    'Integrated Majors & National Oil Companies',
    'Oil & Gas Transportation (tankers, FPSOs, pipeline operators)',
  ],
  Utilities: [
    'Electric Power Generation (Conventional, Renewable)',
    'Electric Transmission & Distribution',
    'Natural Gas Distribution & Storage',
    'Water Supply & Sewage Utilities',
    'Multi-utilities / Integrated Utilities (electric + gas + water)',
  ],
  'Construction & Engineering': [
    'Residential Building Construction (single-family, multifamily, remodeling)',
    'Non-residential Building Construction (commercial, institutional, industrial)',
    'Heavy & Civil Engineering (highways, bridges, rail, airports, marine, utilities infrastructure)',
    'Specialty Trade Contractors (electrical, plumbing, HVAC, roofing, demolition, concrete, glazing)',
    'Engineering, Procurement & Construction (EPC) Services',
    'Construction Project Management & Design-Build',
  ],
  Manufacturing: [
    'Automotive & Auto Parts',
    'Aerospace & Defense',
    'Machinery & Industrial Equipment',
    'Electronics / Semiconductors / EMS',
    'Chemicals (commodity, specialty, agricultural, industrial gases)',
    'Pharmaceuticals & Biotech Manufacturing',
    'Medical Devices & Instruments',
    'Food & Beverage Processing',
    'Textiles, Apparel & Footwear',
    'Wood, Paper & Packaging Products',
    'Plastics & Rubber Products',
    'Glass, Ceramics & Building Materials',
    'Metal Fabrication & Foundries',
    'Printing & Recorded Media',
  ],
  'Transportation, Warehousing & Logistics': [
    'Airlines & Air Cargo',
    'Rail Freight & Passenger',
    'Trucking / Road Freight (less-than-truckload, full truckload, courier & express)',
    'Maritime Transport (container shipping, bulk, tanker, ports & terminals)',
    'Pipeline Transport (oil, gas, product)',
    'Logistics Services (3PL/4PL, freight forwarding, customs brokerage)',
    'Warehousing & Storage (cold chain, bulk, bonded, fulfillment)',
    'Postal & Parcel Delivery',
  ],
  'Information Technology & Software Services': [
    'IT Consulting & Systems Integration',
    'Custom & Enterprise Software (ERP, CRM, SCM, cybersecurity, analytics)',
    'Cloud Computing & IaaS / PaaS (hyperscale, managed cloud, edge)',
    'Data Centers & Colocation',
    'Semiconductor Design (Fabless) & EDA Tools',
    'IT Outsourcing / Managed Services',
    'FinTech & Payments Tech',
    'Gaming Software & Interactive Entertainment',
    'IT Hardware & Peripherals (PCs, servers, storage, networking gear)',
  ],
  Telecommunications: [
    'Wireless Carriers / Mobile Network Operators (MNOs)',
    'Fixed-Line & Broadband Providers',
    'Cable & Satellite TV Operators',
    'Telecom Infrastructure (towercos, small-cell, fiber backhaul, data transmission)',
    'Telecom Equipment Vendors (RAN, core, CPE, optical, test & measurement)',
    'Wholesale Carriers & Interconnect Services (subsea cables, IXPs)',
  ],
  'Healthcare & Life Sciences': [
    'Healthcare Providers (hospitals, clinics)',
    'Pharmaceuticals (innovator, generics, OTC)',
    'Biotechnology',
    'Medical Devices & Diagnostics',
    'Life Science Tools & Contract Research (CROs, CDMOs, lab instruments, reagents)',
    'Healthcare IT & Digital Health',
    'Health Insurance / Managed Care',
    'Long-Term Care & Assisted Living',
  ],
  'Financial Services': [
    'Banking (retail, commercial, corporate, investment, private)',
    'Capital Markets (brokers, dealers, exchanges, market makers)',
    'Asset & Wealth Management (mutual funds, pension funds, hedge funds, private banking)',
    'Investment Banking & Advisory',
    'Consumer & SME Lending / Specialty Finance (credit cards, auto finance, microfinance)',
    'FinTech Platforms (digital banks, robo-advisers, P2P lending)',
    'Payments & Remittances Networks',
  ],
  Insurance: [
    'Life & Annuities',
    'Property & Casualty (P&C)',
    'Health Insurance',
    'Reinsurance',
    'Insurance Brokers & Agencies',
    'Insurance Technology (InsurTech)',
    'Captive & Alternative Risk Transfer',
  ],
  'Real Estate, Property Development & Facilities Management': [
    'Residential Development & Homebuilding',
    'Commercial Development (office, retail, hospitality, mixed-use)',
    'Industrial & Logistics Real Estate',
    'Real Estate Investment Trusts (REITs) (equity, mortgage, specialty)',
    'Property Management & Leasing',
    'Facilities & Asset Management Services (FM, HVAC, janitorial, security, landscaping)',
    'Real Estate Services (brokerage, appraisal, consulting)',
  ],
  'Retail & E-commerce': [
    'Food & Grocery Retail (hypermarkets, supermarkets, convenience)',
    'General Merchandise & Department Stores',
    'Specialty Retail (apparel, electronics, home improvement, auto parts, pharmacies)',
    'Direct-to-Consumer (DTC) Brands',
    'Online Marketplaces & Platform Retailers',
    'Omni-channel Fulfillment & Last-mile Delivery',
    'Duty-Free & Travel Retail',
  ],
  'Consumer Packaged Goods': [
    'Food & Beverage (snacks, beverages, dairy, frozen, functional foods)',
    'Household Care (cleaners, detergents, paper products)',
    'Personal Care & Beauty (skin, hair, oral, cosmetics, fragrances)',
    'Tobacco & Reduced-risk Products',
    'Pet Care Products',
    'Baby, Feminine & Family-care Products',
  ],
  'Media, Entertainment & Publishing': [
    'Broadcast TV & Radio',
    'Cable & Streaming Networks',
    'Film & Television Production / Studios',
    'Music Recording & Publishing',
    'Video Games & Esports',
    'Newspapers, Magazines & Books (Print & Digital)',
    'Advertising & Marketing Services (agencies, ad-tech, out-of-home)',
    'Events, Exhibitions & Live Entertainment',
  ],
  'Hospitality, Travel & Tourism': [
    'Hotels, Resorts & Serviced Apartments',
    'Restaurants & Foodservice (QSR, casual, fine dining, catering)',
    'Cruise Lines & Ferries',
    'Tour Operators & Travel Agencies',
    'Airports & Ground Handling',
    'Theme Parks & Attractions',
    'Short-term Rentals & Vacation Ownership (timeshare)',
  ],
  'Aerospace & Defense': [
    'Commercial Aircraft Manufacturing & Sub-systems',
    'Defense Platforms & Systems (air, land, sea, space)',
    'Maintenance, Repair & Overhaul (MRO)',
    'Avionics & Electronic Warfare',
    'Missiles, Munitions & Propulsion',
    'Space Launch & Satellites',
    'Defense IT & Cybersecurity',
    'Unmanned Aerial Systems (UAS) / Drones',
  ],
  'Education & Training Services': [
    'K-12 Schools (public, charter, private)',
    'Higher Education (universities, colleges, community colleges)',
    'Vocational & Technical Institutes',
    'Corporate & Professional Training',
    'EdTech Platforms & Online Learning (MOOCs, courseware, tutoring)',
    'Test Preparation & Supplemental Education',
    'Early Childhood & Childcare Services',
  ],
  'Professional & Business Services': [
    'Management Consulting & Strategy',
    'Legal Services',
    'Accounting & Audit',
    'Human Resources & Staffing',
    'Architectural & Engineering Services',
    'Marketing, PR & Design Agencies',
    'Facilities & Security Services',
    'Research & Analytics / BPO / KPO',
  ],
};
